import { FC, useCallback, useEffect, useRef } from 'react';
import {
    BoldIcon,
    ItalicIcon,
    UnderlineIcon,
    ListBulletIcon,
    NumberedListIcon,
    LinkIcon,
    H1Icon,
    H2Icon,
} from '@heroicons/react/24/outline';

interface RichTextEditorProps {
    value: string;
    onChange: (html: string) => void;
    placeholder?: string;
}

type Command = { cmd: string; arg?: string; icon: FC<{ className?: string }>; label: string };

const COMMANDS: Command[] = [
    { cmd: 'bold', icon: BoldIcon, label: 'Gras' },
    { cmd: 'italic', icon: ItalicIcon, label: 'Italique' },
    { cmd: 'underline', icon: UnderlineIcon, label: 'Souligné' },
    { cmd: 'formatBlock', arg: 'H2', icon: H1Icon, label: 'Titre' },
    { cmd: 'formatBlock', arg: 'H3', icon: H2Icon, label: 'Sous-titre' },
    { cmd: 'insertUnorderedList', icon: ListBulletIcon, label: 'Liste à puces' },
    { cmd: 'insertOrderedList', icon: NumberedListIcon, label: 'Liste numérotée' },
];

/**
 * Éditeur WYSIWYG minimal basé sur contentEditable + document.execCommand.
 * Produit du HTML simple (titres, gras, listes, liens) pour les textes légaux.
 */
export const RichTextEditor: FC<RichTextEditorProps> = ({ value, onChange, placeholder }) => {
    const editorRef = useRef<HTMLDivElement>(null);

    // Synchronise le HTML entrant uniquement quand il diffère (évite de casser le curseur).
    useEffect(() => {
        const el = editorRef.current;
        if (el && el.innerHTML !== value) {
            el.innerHTML = value || '';
        }
    }, [value]);

    const handleInput = useCallback(() => {
        if (editorRef.current) onChange(editorRef.current.innerHTML);
    }, [onChange]);

    const exec = useCallback((cmd: string, arg?: string) => {
        editorRef.current?.focus();
        document.execCommand(cmd, false, arg);
        handleInput();
    }, [handleInput]);

    const insertLink = useCallback(() => {
        const url = window.prompt('URL du lien :', 'https://');
        if (url) exec('createLink', url);
    }, [exec]);

    return (
        <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
            <div className="flex flex-wrap items-center gap-1 px-2 py-1.5 border-b border-gray-100 bg-gray-50">
                {COMMANDS.map((c) => (
                    <button
                        key={c.label}
                        type="button"
                        title={c.label}
                        onMouseDown={(e) => { e.preventDefault(); exec(c.cmd, c.arg); }}
                        className="p-1.5 rounded-md text-gray-600 hover:bg-gray-200 hover:text-gray-900 transition-colors"
                    >
                        <c.icon className="w-4 h-4" />
                    </button>
                ))}
                <button
                    type="button"
                    title="Lien"
                    onMouseDown={(e) => { e.preventDefault(); insertLink(); }}
                    className="p-1.5 rounded-md text-gray-600 hover:bg-gray-200 hover:text-gray-900 transition-colors"
                >
                    <LinkIcon className="w-4 h-4" />
                </button>
            </div>
            <div
                ref={editorRef}
                contentEditable
                onInput={handleInput}
                data-placeholder={placeholder}
                className="prose prose-sm max-w-none min-h-[320px] px-4 py-3 text-sm text-gray-800 focus:outline-none empty:before:content-[attr(data-placeholder)] empty:before:text-gray-400"
                suppressContentEditableWarning
            />
        </div>
    );
};
